<template>
	<div>
		<div class="text-recommendation-empty">
			<span class="text-recommendation-empty__text">
				{{ $t('WEBAPP_WHY_TO_WATCH_EMPTY_SUBHEADING') }}
			</span>

			<div class="text-recommendation-empty__action">
				<InputText
					class="input"
					v-model="headline"
					:placeholder="$t('WEBAPP_WHY_TO_WATCH_HEADLINE_PLACEHOLDER')"
					@focus="() => trackWtw('headlineFocused')"
					@blur="() => trackWtw('headlineBlurred', { checkArgs: headline })"
				/>

				<BasicButton
					class="submit"
					color="primary"
					:disabled="headline.trim().length === 0"
					@click="() => toggleTextRecommendationModal(true)"
				>
					{{ $t('WEBAPP_NEXT') }}
				</BasicButton>
			</div>

			<div class="text-recommendation-empty__background">
				<img :src="backgroundImageUrl" />
			</div>
		</div>

		<TextRecommendationModalV2
			v-if="displayTextRecommendationModal"
			:title="title"
			:form-data="{ headline }"
			@close="toggleTextRecommendationModal(false)"
		/>
	</div>
</template>

<script setup lang="ts">
import type { TitleDetail } from '@/interfaces/title-details-graphql';

import { TextRecommendationModalV2 } from '@/lazy-load-components';
import BasicButton from '@/ui-components/BasicButton.vue';
import InputText from '@/features/ui/components/InputText.vue';

import { AccountModes } from '@/enums/login';
import { disableScroll, enableScroll } from '@/helpers/scroll-helper';
import { preventNonSignedInAction } from '@/helpers/prevent-non-signed-in-action-helper';

import { computed, ref } from 'vue';
import { useWtwTracking } from '../tracking';

const props = defineProps<{ title: TitleDetail }>();

const { jwConfig } = useRuntimeConfig().public;

const headline = ref('');

const displayTextRecommendationModal = ref<boolean>(false);

const backgroundImageUrl = computed(() => jwConfig.IMAGESCALER_URL + props.title.content.fullPosterUrl);

const { trackWtw } = useWtwTracking({ title: () => props.title });

function toggleTextRecommendationModal(value: boolean) {
	if (preventNonSignedInAction(AccountModes.REGISTRATION_OPTIONS)) return;

	displayTextRecommendationModal.value = value;
	displayTextRecommendationModal.value ? disableScroll() : enableScroll();

	if (displayTextRecommendationModal.value) trackWtw('emptyNextClick');
}
</script>

<style lang="scss" scoped>
.text-recommendation-empty {
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 16px;
	min-height: 144px;
	border-radius: 8px;
	padding: 24px;

	background: linear-gradient(180deg, rgba(12, 19, 30, 0.8) 0%, #0c131e 100%);
	overflow: hidden;

	&__text {
		color: var(--text-text-low, #909398);
		font-size: 16px;
		font-style: normal;
		font-weight: 400;
		line-height: 24px;
		letter-spacing: 0.24px;
	}

	&__action {
		display: flex;
		gap: 16px;

		.input {
			flex-grow: 1;
			border: 1px solid var(--border-border-ghost, rgba(255, 255, 255, 0.08));
		}
	}

	&__background {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-size: cover;
		background-position: center;

		filter: blur(50px);

		z-index: -1;
	}
}
</style>
